import { ProactiveMsgDto } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { flow, isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { EditInputFieldState } from "@fidget/common/components/form/editInput";
import { LanguageOption } from "@fidget/common/components/form/languagePicker/languagePicker";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { TagPicker } from "../../forms/tagPicker/tagPicker";
import { usePreloader } from "../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../models/useSettingsList/useSettingsList";
import { SettingsForm } from "../settingsForm";
import { DeleteConfirmationToastOnError } from "../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormSaveDiscardIcon } from "../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { BooleanListFilter } from "../settingsForms/filters/booleanListFilter";
import { ListFilterWrapper } from "../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import {
  ListActions,
  ListDeleteAction,
  ListBooleanAction,
  ListActionsSection
} from "../listActions";
import { VerticalSeparator } from "../settingsForms/separators/verticalSeparator/verticalSeparator";
import { ProactiveMsgBox } from "./components/proactiveMsgBox/proactiveMsgBox";
import styles from "./proactiveMessagesList.css";
import { ExistingProactiveFormType, NewProactiveFormType } from "./types";
import {
  copyExistingProactiveMsgForm,
  createNewProactiveMsgForm,
  mapExistingFormToUpdateForm,
  mapNewFormToCreateForm,
  mapProactiveMsgToForm
} from "./utils";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../models/agentServices";

interface Props {
  pickedLanguage: LanguageOption;
}

const ProactiveMessagesListComponent = (props: Props) => {
  const {
    newItem,
    handleCreateNewClick,
    discardNewItem,
    saveNewItem,

    items,
    filteredItems,
    forEachSelected,
    forAllSelected,
    selectedItems,
    onDiscardAll,
    onSubmitAll,
    setItemsSelectedOnPredicate,
    setItemSelected,
    editedItems,
    deleteAllSelected,
    apiDeleteItem,
    uiRemoveExistingItem,
    onExistingEditClick,

    currentlyEditedItem,
    discardEditedItem,
    saveEditedItemChangesLocally
  } = useProactiveListState(props);

  const filterdPickedLangItems = filteredItems.filter(
    (item) => item.$.langTag.$ === props.pickedLanguage
  );

  const renderNewMessageForm = (form: NewProactiveFormType) => {
    const { message, tagId } = form.$;
    return (
      <ElementForm isEdited>
        <div className={styles["form-content"]}>
          <div className={classNames(styles["input-container"], styles["tag-input"])}>
            <div className={classNames(styles["form-text"], styles["form-text-dimmed"])}>
              <L10n id={"settings.proactiveMessage.addNew.tag.title"} />
            </div>
            <div className={styles["tag-picker"]}>
              <TagPicker tagId={tagId.$} onChange={(tag) => tagId.reset(tag.id)} />
            </div>
          </div>
          <VerticalSeparator />
          <div className={classNames(styles["input-container"], styles["message-input"])}>
            <div className={classNames(styles["form-text"], styles["form-text-dimmed"])}>
              <L10n id={"settings.proactiveMessage.addNew.message.title"} />
            </div>
            <EditInputFieldState
              className={classNames(styles["message-margin"])}
              field={message}
              placeholder={intl.formatMessage(
                "settings.proactiveMessage.addNew.message.placeholder",
                { limit: staticConfig.proactiveMessageMaxCharacters }
              )}
              maxLength={staticConfig.proactiveMessageMaxCharacters}
            />
          </div>
        </div>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderAddNewPlaceholder = () => (
    <FormPlaceholder
      onClick={handleCreateNewClick}
      text={intl.formatMessage("settings.proactiveMessages.addNew")}
    />
  );

  const renderNewMessageComponent = () =>
    newItem ? renderNewMessageForm(newItem) : renderAddNewPlaceholder();

  const renderElementForm = (item: ExistingProactiveFormType, onDeleteClick: () => void) => {
    const isCurrentlyEdited = !!currentlyEditedItem && item.$.id.$ === currentlyEditedItem.$.id.$;
    const currentForm = isCurrentlyEdited && currentlyEditedItem ? currentlyEditedItem : item;

    return (
      <ElementForm isEdited={isCurrentlyEdited}>
        {!isCurrentlyEdited && (
          <>
            <ElementFormActions>
              <ElementFormCheckbox
                id={item.$.id.$}
                isSelected={!!selectedItems[item.$.id.$]}
                onChange={(val) => setItemSelected(item, val)}
              />
            </ElementFormActions>
            <VerticalSeparator />
          </>
        )}
        <ProactiveMsgBox form={currentForm} isEdited={isCurrentlyEdited} />
        <VerticalSeparator />
        {isCurrentlyEdited ? (
          <ElementFormActions>
            <ElementFormSaveDiscardIcon
              onSave={saveEditedItemChangesLocally}
              onDiscard={discardEditedItem}
            />
          </ElementFormActions>
        ) : (
          <ElementFormActions>
            <ElementFormEditIcon onClick={() => onExistingEditClick(item)} />
            <ElementFormDeleteIcon onClick={onDeleteClick} />
          </ElementFormActions>
        )}
      </ElementForm>
    );
  };

  const renderExistingList = () => {
    return filterdPickedLangItems.map((item) => {
      return (
        <DeleteConfirmationToastOnError
          key={item.$.id.$}
          areYouSureText={intl.formatMessage("settings.proactiveMessage.areYouSureToRemove")}
          succesfullyDeletedText={intl.formatMessage(
            "settings.proactiveMessage.deleteConfirmation"
          )}
          onDelete={() => apiDeleteItem(item.$.id.$)}
          onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
          renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
        />
      );
    });
  };

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingProactiveFormType>>
        <ListFilterWrapper<ExistingProactiveFormType>
          placeholder={intl.formatMessage("settings.tags.filters.placeholder")}
          allCount={items.length}
          filteredCount={filterdPickedLangItems.length}
          inputPredicateId="TagInputFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.tag.$.tag.$, input)}
          headerText={intl.formatMessage("settings.proactiveMessage.filters.header")}
        >
          <BooleanListFilter<ExistingProactiveFormType>
            description={intl.formatMessage("settings.proactiveMessage.filters.displayed")}
            filterId="ProactiveActive"
            predicate={(item) => item.$.enabled.$}
          />
          <BooleanListFilter<ExistingProactiveFormType>
            description={intl.formatMessage(
              "settings.proactiveMessage.filters.displayedOutsideHours"
            )}
            filterId="ProactiveOutside"
            predicate={(item) => item.$.displayOutsideWorkingHours.$}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected =
      !!filterdPickedLangItems.length && size(selectedItems) === size(filterdPickedLangItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) =>
            setItemsSelectedOnPredicate(
              filteredItems,
              val,
              (item) => item.$.langTag.$ === props.pickedLanguage
            )
          }
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            <ListBooleanAction
              text={intl.formatMessage("settings.proactiveMessage.displayMessage")}
              isActive={forAllSelected((item) => item.$.enabled.$)}
              onClick={(val) => forEachSelected((i) => i.$.enabled.reset(val))}
            />
            <ListBooleanAction
              text={intl.formatMessage("settings.proactiveMessage.displayOutsideHours")}
              isActive={forAllSelected((item) => item.$.displayOutsideWorkingHours.$)}
              onClick={(val) => forEachSelected((i) => i.$.displayOutsideWorkingHours.reset(val))}
            />
          </ListActionsSection>
          <VerticalSeparator />
          <ListDeleteAction onDelete={deleteAllSelected} />
        </ListActions>
      </SettingsForm>
    );
  };

  return (
    <>
      {renderNewMessageComponent()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderExistingList()}
    </>
  );
};
export const ProactiveMessagesList = observer(ProactiveMessagesListComponent);

type HookParams = Params<ExistingProactiveFormType, NewProactiveFormType, ProactiveMsgDto>;
type HookEditable = ListHookEditable<ExistingProactiveFormType, NewProactiveFormType>;
type HookSelectable = ListHookWithSelectables<
  ExistingProactiveFormType,
  NewProactiveFormType,
  HookEditable
>;
type HookFilterable = ListHookWithFilters<
  ExistingProactiveFormType,
  NewProactiveFormType,
  HookSelectable
>;

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

const useProactiveListState = (props: Props) => {
  const { spinnerService } = useAgentServices();
  const { setLoadingState } = usePreloader();

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems: () => spinnerService.getProactiveMessages(),
    apiCreateItem: (form) =>
      spinnerService.createProactiveMessage(mapNewFormToCreateForm(form, props.pickedLanguage)),
    apiUpdateItem: (form) =>
      spinnerService.updateProactiveMessage(form.$.id.$, mapExistingFormToUpdateForm(form)),
    apiDeleteItem: (id) => spinnerService.deleteProactiveMessage(id),
    mapItemToForm: mapProactiveMsgToForm,
    createNewForm: createNewProactiveMsgForm,
    copyExistingForm: copyExistingProactiveMsgForm,
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,

    saveErrorId: "settings.proactiveMessage.error.wrongNewMessage",
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.enabled.onUpdate(setEdited);
      item.$.displayOutsideWorkingHours.onUpdate(setEdited);
    });
  }, [listHook.items]);

  return listHook;
};
